import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { TooltipProps } from 'recharts'
import {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent'
import { SpendType } from 'src/components/SpendGraph/SpendGraph'
import { ExtendCurrency, ExtendLocales } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectDefaultCurrencyFromState } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface HomepageChartTooltipProps extends TooltipProps<ValueType, NameType> {
  fill: string
  fillPhysical: string
  fillBillPay: string
}

const HomepageChartTooltip: React.FC<HomepageChartTooltipProps> = (
  props,
): React.ReactElement | null => {
  const currencyFormatter = useCurrencyFormatter()
  const user = useSelector(selectUserListModelFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const defaultCurrency = useSelector(selectDefaultCurrencyFromState)

  if (props.active && props.payload?.length) {
    const tooltipPayload = props.payload?.[0]

    const dualCurrency =
      tooltipPayload?.payload.usd !== undefined &&
      tooltipPayload?.payload.cad !== undefined

    // TODO: eslint tech debt

    const { usd, cad, usdPhysical, cadPhysical, usdBillPay, cadBillPay } =
      // eslint-disable-next-line no-unsafe-optional-chaining
      tooltipPayload?.payload

    const totalUsd = (usd ?? 0) + (usdPhysical ?? 0) + (usdBillPay ?? 0)
    const totalCad = (cad ?? 0) + (cadPhysical ?? 0) + (cadBillPay ?? 0)

    const selectedSpendType =
      tooltipPayload?.payload.selectedSpendType || SpendType.none
    const selectedCurrency = tooltipPayload?.payload.selectedCurrency || 'usd'

    const renderCurrencyBlock = (
      label: string,
      usdValue: number,
      cadValue: number,
      locale: ExtendLocales,
      dualCurrency: boolean,
      defaultCurrency: ExtendCurrency,
      chipColor: string,
      spendType: SpendType,
      selectedSpendType: SpendType,
      selectedCurrency: string,
    ): JSX.Element | undefined => {
      const value = ((selectedCurrency === 'cad' ? cadValue : usdValue) ??
        0) as number
      return value !== 0 ? (
        <div
          css={css`
            display: flex;
            flex-direction: row;
            margin-top: 10px;
            align-items: top;
            opacity: ${spendType === selectedSpendType ? 1.0 : 0.5};
          `}
        >
          <div
            css={css`
              height: 10px;
              width: 10px;
              border-radius: 2px;
              background-color: ${chipColor};
              margin-top: 5px;
              margin-right: 10px;
            `}
          />
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <Trns source={label} />
            {renderCurrency(
              selectedCurrency,
              usdValue,
              cadValue,
              locale,
              dualCurrency,
              defaultCurrency,
            )}
          </div>
        </div>
      ) : undefined
    }

    const renderCurrency = (
      selectedCurrency: string,
      usdValue: number,
      cadValue: number,
      locale: ExtendLocales,
      dualCurrency: boolean,
      defaultCurrency: ExtendCurrency,
    ): JSX.Element | undefined => (
      <div
        css={css`
          ${getFontCss(
            12,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {currencyFormatter({
          amount: selectedCurrency === 'cad' ? cadValue : (usdValue as number),
          currency:
            selectedCurrency === 'cad'
              ? ExtendCurrency.Cad
              : ExtendCurrency.Usd,
          forceShowCurrency: dualCurrency,
        })}
      </div>
    )

    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
        `}
      >
        <div
          css={css`
            border-radius: 8px;
            background: white;
            border-left: 5px solid ${themeConstants.colors.cta};
            box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.1);
            padding: 12px;
            min-width: 250px;
          `}
        >
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <Trns source={'Total card spend:'} />
            {renderCurrency(
              selectedCurrency,
              totalUsd,
              totalCad,
              user?.locale || locale,
              dualCurrency,
              defaultCurrency,
            )}
            <Fragment>
              {renderCurrencyBlock(
                'Virtual cards:',
                usd,
                cad,
                user?.locale || locale,
                dualCurrency,
                defaultCurrency,
                props.fill,
                SpendType.virtual,
                selectedSpendType,
                selectedCurrency,
              )}
              {renderCurrencyBlock(
                'Bill pay cards:',
                usdBillPay,
                cadBillPay,
                user?.locale || locale,
                dualCurrency,
                defaultCurrency,
                props.fillBillPay,
                SpendType.billPay,
                selectedSpendType,
                selectedCurrency,
              )}
              {renderCurrencyBlock(
                'Physical cards:',
                usdPhysical,
                cadPhysical,
                user?.locale || locale,
                dualCurrency,
                defaultCurrency,
                props.fillPhysical,
                SpendType.physical,
                selectedSpendType,
                selectedCurrency,
              )}
            </Fragment>
          </div>
        </div>
      </div>
    )
  }

  return null
}

export default HomepageChartTooltip
